import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { useReportId, useReportsContext } from "@ryde/redux";
import {
  getIsFlexPeriodGroupingApplied,
  hasKeys,
  reportFlexGroupingParamsAF,
  reportGroupingPeriodAF,
} from "@ryde/shared";

import { REPORT_CHART_GROUPING_DIMENSION_VALUE } from "./report-chart.constants";
import { IReportChartAxisOption } from "./report-chart.models";
import {
  aggregateReportRows,
  aggregateReportRowsByReportGrouping,
  buildReportChartDimensionOptions,
  getReportGroupingDimensionColumns,
  isMetricColumn,
  mapColumnToAxisOption,
} from "./report-chart.utils.data";
import { useReportChartDimensionChange } from "./use-report-chart-dimension-change";

export const useReportChartData = () => {
  const { t } = useTranslation();
  const { reportId } = useReportId();
  const { reportDto, reportTitle, reportTemplate } = useReportsContext();
  const groupingParamKeys = useRecoilValue(
    reportFlexGroupingParamsAF(reportId),
  );
  const periodGrouping = useRecoilValue(reportGroupingPeriodAF(reportId));

  const columns = useMemo(
    () => reportDto?.layout?.columns ?? [],
    [reportDto?.layout?.columns],
  );
  const rows = useMemo(() => reportDto?.rows ?? [], [reportDto?.rows]);

  const flexGroupingTemplateKeys = useMemo(() => {
    if (
      !reportTemplate?.flexGroupingColumns ||
      !hasKeys(reportTemplate.flexGroupingColumns)
    ) {
      return [];
    }

    return Object.keys(reportTemplate.flexGroupingColumns);
  }, [reportTemplate?.flexGroupingColumns]);

  const isFlexPeriodApplied = useMemo(
    () =>
      getIsFlexPeriodGroupingApplied(reportTemplate?.periodGroupingColumn, {
        flexGrouping: { periodGrouping },
      }),
    [periodGrouping, reportTemplate?.periodGroupingColumn],
  );

  const hasFlexGroupingTemplate = Boolean(
    reportTemplate?.flexGroupingColumns &&
    hasKeys(reportTemplate.flexGroupingColumns),
  );

  const groupingDimensionColumns = useMemo(
    () =>
      getReportGroupingDimensionColumns({
        columns,
        groupingParamKeys,
        isFlexPeriodApplied,
        hasFlexGroupingTemplate,
      }),
    [columns, groupingParamKeys, hasFlexGroupingTemplate, isFlexPeriodApplied],
  );

  const hasReportGroupingDimension = groupingDimensionColumns.length > 0;

  const dimensionOptions: IReportChartAxisOption[] = useMemo(
    () =>
      buildReportChartDimensionOptions({
        columns,
        groupingParamKeys,
        flexGroupingTemplateKeys,
        flexGroupingColumns: reportTemplate?.flexGroupingColumns,
        periodGroupingColumn: reportTemplate?.periodGroupingColumn,
        hasReportGroupingDimension,
        t,
      }),
    [
      columns,
      flexGroupingTemplateKeys,
      groupingParamKeys,
      hasReportGroupingDimension,
      reportTemplate?.flexGroupingColumns,
      reportTemplate?.periodGroupingColumn,
      t,
    ],
  );

  const metricOptions: IReportChartAxisOption[] = useMemo(
    () =>
      columns.filter(isMetricColumn).map(col => mapColumnToAxisOption(col, t)),
    [columns, t],
  );

  const [dimensionColumnKey, setDimensionColumnKey] = useState(
    () => dimensionOptions[0]?.value ?? "",
  );
  const [metricColumnKey, setMetricColumnKey] = useState(
    () => metricOptions[0]?.value ?? "",
  );

  const { onDimensionChange } = useReportChartDimensionChange({
    groupingParamKeys: groupingParamKeys ?? [],
    flexGroupingTemplateKeys,
    isFlexPeriodApplied,
    setDimensionColumnKey,
  });

  useEffect(() => {
    if (
      dimensionOptions.length &&
      !dimensionOptions.some(o => o.value === dimensionColumnKey)
    ) {
      setDimensionColumnKey(dimensionOptions[0].value);
    }
  }, [dimensionColumnKey, dimensionOptions]);

  useEffect(() => {
    if (
      metricOptions.length &&
      !metricOptions.some(o => o.value === metricColumnKey)
    ) {
      setMetricColumnKey(metricOptions[0].value);
    }
  }, [metricColumnKey, metricOptions]);

  const resolvedDimensionKey =
    dimensionOptions.find(o => o.value === dimensionColumnKey)?.value ??
    dimensionOptions[0]?.value ??
    "";

  const isReportGroupingDimension =
    resolvedDimensionKey === REPORT_CHART_GROUPING_DIMENSION_VALUE;

  const selectedDimensionColumn = useMemo(() => {
    if (isReportGroupingDimension) {
      return undefined;
    }

    return columns.find(col => col.uiTableColumn === resolvedDimensionKey);
  }, [columns, isReportGroupingDimension, resolvedDimensionKey]);

  const resolvedMetricKey =
    metricOptions.find(o => o.value === metricColumnKey)?.value ??
    metricOptions[0]?.value ??
    "";

  const chartContent = useMemo(() => {
    if (!resolvedDimensionKey || !resolvedMetricKey || !rows.length) {
      return { entities: [] };
    }

    if (isReportGroupingDimension) {
      return aggregateReportRowsByReportGrouping({
        rows,
        groupingColumns: groupingDimensionColumns,
        metricColumnKey: resolvedMetricKey,
      });
    }

    return aggregateReportRows({
      rows,
      dimensionColumnKey: resolvedDimensionKey,
      metricColumnKey: resolvedMetricKey,
    });
  }, [
    groupingDimensionColumns,
    isReportGroupingDimension,
    resolvedDimensionKey,
    resolvedMetricKey,
    rows,
  ]);

  const reportSupportsChart = Boolean(
    reportTemplate?.periodGroupingColumn ||
    (reportTemplate?.flexGroupingColumns &&
      hasKeys(reportTemplate.flexGroupingColumns)),
  );

  const hasChartLayout =
    dimensionOptions.length > 0 && metricOptions.length > 0;

  const hasChartCapability = rows.length > 0 && hasChartLayout;

  const isChartButtonVisible =
    !reportDto?.groupingParamsExpected && reportSupportsChart && hasChartLayout;

  return {
    reportTitle,
    dimensionOptions,
    metricOptions,
    dimensionColumnKey: resolvedDimensionKey,
    selectedDimensionColumn,
    isReportGroupingDimension,
    groupingDimensionColumns,
    metricColumnKey: resolvedMetricKey,
    onDimensionChange,
    setMetricColumnKey,
    chartContent,
    hasChartCapability,
    isChartButtonVisible,
    columns,
  };
};
